import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { MemberLookupResponse } from '@shared/models/member-lookup.response';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { combineLatest, map, Observable } from 'rxjs';
import { ApiCacheService } from './api-cache.service';
import { AuthService } from './auth.service';
import { AlertService } from './alert/alert.service';

@Injectable({
  providedIn: 'root',
})
export class MemberLookupService {
  url = `${environment.crewDetailUrl}`;

  constructor(
    private apiCacheService: ApiCacheService,
    private alertService: AlertService,
    private authService: AuthService,
  ) {}

  getContactInfo(
    empNumber: string,
  ): Observable<RequestState<MemberLookupResponse>> {
    const cacheId = `contact-${empNumber}`;
    const usersEmpNum = this.authService.empNumber;
    const postBody = {
      request: 0,
      searchTerm: empNumber,
      userId: usersEmpNum,
    };

    return this.apiCacheService
      .request<MemberLookupResponse>(cacheId, this.url, postBody)
      .pipe(
        map((response) => {
          if (response.hasError) {
            this.showErrorAlert();
          }

          if (response.response) {
            return response.into<MemberLookupResponse>(
              this.validatePhoneNumbers(response.response),
            );
          }

          return response;
        }),
      );
  }

  getMultipleContacts(
    empNumbers: string[],
  ): Observable<RequestState<MemberLookupResponse[]>> {
    const requests = empNumbers.map((empNumber) =>
      this.getContactInfo(empNumber),
    );

    return combineLatest(requests).pipe(
      map((responses) => {
        for (const response of responses) {
          // If any responses are loading or errored, pass on state to client.
          if (response.isLoading) {
            return new RequestState<MemberLookupResponse[]>(
              RequestStatus.IsLoadingNoResponse,
            );
          }

          if (response.hasError) {
            return new RequestState<MemberLookupResponse[]>(
              RequestStatus.HasErrorNoResponse,
            );
          }
        }

        const results = responses
          .map((response) => response.response)
          .filter((response) => response !== null && response !== undefined);

        return new RequestState<MemberLookupResponse[]>(
          RequestStatus.IsCompleteWithResponse,
          results,
        );
      }),
    );
  }

  /**
   *  If a phone number isn't present or contains text like "emergency use only", then
   *  the field is replaced with an empty string.
   */
  private validatePhoneNumbers(
    response: MemberLookupResponse,
  ): MemberLookupResponse {
    const cellNumber = response.cell;
    const phoneNumber = response.telephone;

    // Rough test to see if text contains numbers. Response can also
    // contain text like "no public number" or "emergency use only".
    // Phone numbers should be in the format (XXX) XXX-XXXX, but it can't
    // be guaranteed. If at least 3 numbers in a row are present, it is
    // considered a valid phone number.
    if (!cellNumber || !/\d{3}/.test(cellNumber)) {
      response.cell = '';
    }

    if (!phoneNumber || !/\d{3}/.test(phoneNumber)) {
      response.telephone = '';
    }

    return response;
  }

  private showErrorAlert(): void {
    this.alertService.setAlert({
      type: 'error',
      message: 'Unable to load pilot contact information.',
    });
  }
}
